import React, { useState, useEffect } from 'react';
import {
    Grid,
    Button,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    DialogContent
} from '@mui/material';
import { Add } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { fuelCardsApi, FuelCard } from '../services/fuelCardsApi';
import { vehiclesApi, Vehicle } from '../services/vehiclesApi';
import { driversApi, Driver } from '../services/driversApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';

const FuelCards: React.FC = () => {
    const { t } = useLanguage();
    
    
    const [cards, setCards] = useState<FuelCard[]>([]);
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingCard, setEditingCard] = useState<FuelCard | null>(null);
    const [formData, setFormData] = useState<Partial<FuelCard>>({
        cardNumber: '',
        vehicleId: undefined,
        driverId: undefined,
        globalCost: 0,
        pricePerLiter: 130
    });

    const loadData = async () => {
        try {
            const [cardsRes, vehRes, drvRes] = await Promise.all([
                fuelCardsApi.getAll(),
                vehiclesApi.getAll(),
                driversApi.getAll()
            ]);
            
            if (cardsRes.success) setCards(cardsRes.data);
            if (vehRes.success) setVehicles(vehRes.data);
            if (drvRes.success) setDrivers(drvRes.data);
        } catch (error) {
            console.error('Failed to load cards data:', error);
            blobticMessage.error(t('common.messages.error'), 'Failed to load fuel cards data');
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpenDialog = (card?: FuelCard) => {
        if (card) {
            setEditingCard(card);
            setFormData({ ...card });
        } else {
            setEditingCard(null);
            setFormData({
                cardNumber: '',
                vehicleId: undefined,
                driverId: undefined,
                globalCost: 0,
                pricePerLiter: 130
            });
        }
        setIsDialogOpen(true);
    };

    const handleSave = async () => {
        if (!formData.cardNumber) {
            blobticMessage.error(t('common.messages.warning') || 'Warning', t('zoom.check.fillRequired'));
            return;
        }

        try {
            let response;
            if (editingCard) {
                response = await fuelCardsApi.update(editingCard.id, formData);
            } else {
                response = await fuelCardsApi.create(formData);
            }

            if (response.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.saved'));
                setIsDialogOpen(false);
                loadData();
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            console.error('Error saving fuel card:', error);
            blobticMessage.error(t('common.messages.error'), t('common.messages.error'));
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(t('common.confirm.delete') || 'Are you sure?')) return;
        try {
            const response = await fuelCardsApi.delete(id);
            if (response.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.deleted_success'));
                loadData();
            } else {
                blobticMessage.error(t('common.messages.error') || 'Error', response.message || 'Unknown error');
            }
        } catch (error) {
            blobticMessage.error(t('common.messages.error') || 'Error', t('common.messages.delete_error'));
        }
    };

    const columns = [
        { id: 'cardNumber', label: t('parc.fuelcards.cardNumber') || 'Numéro de Carte', minWidth: 150 },
        { id: 'vehicleRegistration', label: t('parc.fuelcards.vehicle') || 'Véhicule Assigné', minWidth: 150 },
        { id: 'driverName', label: t('parc.fuelcards.driver') || 'Conducteur Assigné', minWidth: 180 },
        { id: 'globalCost', label: t('parc.fuelcards.globalCost') || 'Coût Global Consommé', minWidth: 180, format: (val: number) => `${val.toFixed(2)} DZD` },
        { id: 'pricePerLiter', label: t('parc.fuelcards.pricePerLiter') || 'Prix de base/Litre', minWidth: 150, format: (val: number) => `${val} DZD` }
    ];

    const additionalActions = (
        <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
        >
            {t('parc.fuelcards.newCard')}
        </Button>
    );

    return (
        <GenericPage title={t('menu.parc.fuelcards') || 'Cartes Carburant'} additionalActions={additionalActions} footerEnabled={false} disableScroll>
            <GenericTable
                columns={columns}
                data={cards}
                onEdit={handleOpenDialog}
                onDelete={(v: any) => handleDelete(v.id)}
            />

            <RootDialogPage
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                title={editingCard ? t('parc.fuelcards.editCard') : t('parc.fuelcards.addCard')}
                onAction={handleSave}
            >
                <DialogContent>
                    <Grid container spacing={2} sx={{ pt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t('parc.fuelcards.cardNumber')}
                                value={formData.cardNumber}
                                onChange={(e) => setFormData({ ...formData, cardNumber: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.fuelcards.vehicle')}</InputLabel>
                                <Select
                                    value={formData.vehicleId || ''}
                                    label={t('parc.fuelcards.vehicle')}
                                    onChange={(e) => setFormData({ ...formData, vehicleId: e.target.value ? Number(e.target.value) : undefined })}
                                >
                                    <MenuItem value=""><em>{t('parc.fuel.none')}</em></MenuItem>
                                    {vehicles.map((v) => (
                                        <MenuItem key={v.id} value={v.id}>
                                            {v.registrationNumber} ({v.brand})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>{t('parc.fuelcards.driver')}</InputLabel>
                                <Select
                                    value={formData.driverId || ''}
                                    label={t('parc.fuelcards.driver')}
                                    onChange={(e) => setFormData({ ...formData, driverId: e.target.value ? Number(e.target.value) : undefined })}
                                >
                                    <MenuItem value=""><em>{t('parc.fuel.none')}</em></MenuItem>
                                    {drivers.map((d) => (
                                        <MenuItem key={d.id} value={d.id}>
                                            {d.firstName} {d.lastName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('parc.fuelcards.pricePerLiter')}
                                value={formData.pricePerLiter || ''}
                                onChange={(e) => setFormData({ ...formData, pricePerLiter: Number(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('parc.fuelcards.globalCost')}
                                value={formData.globalCost || 0}
                                onChange={(e) => setFormData({ ...formData, globalCost: Number(e.target.value) })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
            </RootDialogPage>
        </GenericPage>
    );
};

export default FuelCards;
